<template>
  <div class="flex flex-col gap-2 w-full">
    <label
      :for="id"
      class="flex justify-between text-xs dark:text-white text-dark2"
      >{{ label }}
      <div class="flex items-center gap-2">
        <span
          v-if="!required"
          data-testId="optional"
          class="text-xs px-1 dark:text-dark4 text-dark2"
          >Optional</span
        >
        <span v-if="showCount" class="text-xs dark:text-dark4 text-dark2"
          >{{ textLength }}/160</span
        >
      </div>
    </label>
    <div
      class="bg-dark4 bg-opacity-5 border border-transparent hover:[&:not(:has(textarea:disabled))]:border-dark4 hover:[&:not(:has(textarea:disabled))]:border-opacity-50 [&:has(textarea:focus)]:border-dark4 [&:has(textarea:focus)]:border-opacity-50 duration-150 rounded-sm"
    >
      <div class="relative">
        <div
          v-if="highlightUrls"
          ref="overlayRef"
          class="absolute inset-0 pointer-events-none overflow-hidden whitespace-pre-wrap break-words py-2 px-3 text-dark2 dark:text-white"
          aria-hidden="true"
          v-html="highlightedHtml"
        />
        <textarea
          ref="textareaRef"
          :id="id"
          v-model="model"
          :name="name"
          v-bind="$attrs"
          :maxlength="maxlength"
          :class="[
            'w-full py-2 px-3 dark:text-white dark:focus:text-white dark:bg-opacity-5 text-dark2 focus:text-dark2 placeholder:text-dark4 placeholder:opacity-50 focus:outline-none placeholder:leading-5 placeholder:text-md bg-transparent',
            highlightUrls
              ? 'text-transparent dark:text-transparent caret-dark2 dark:caret-white'
              : ''
          ]"
          :placeholder="placeholder"
          @scroll="onTextareaScroll"
        />
      </div>
    </div>

    <span
      v-if="note"
      class="dark:text-dark4 text-dark1 text-opacity-70 text-xs"
      >{{ note }}</span
    >
    <span v-if="error" class="text-red-400">{{ error }}</span>
  </div>
</template>

<script setup lang="ts">
  import { useUrlHighlighter } from '@/composables';
  import { computed } from 'vue';

  interface TextareaProps {
    id: string;
    label?: string;
    name: string;
    placeholder?: string;
    note?: string;
    required?: boolean;
    error?: string;
    showCount?: boolean;
    highlightUrls?: boolean;
  }

  const props = withDefaults(defineProps<TextareaProps>(), {
    placeholder: '',
    required: false,
    label: '',
    note: '',
    showCount: false,
    error: '',
    highlightUrls: false
  });
  const model = defineModel<string>();
  const textLength = computed(() => model.value?.length || 0);
  const maxlength = computed(() => (props.showCount ? 160 : undefined));

  const { textareaRef, overlayRef, highlightedHtml, onTextareaScroll } =
    useUrlHighlighter(model, { enabled: computed(() => props.highlightUrls) });
</script>
